import { memo, useMemo, useCallback } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    Box,
    type SelectChangeEvent,
} from '@mui/material';
import { type SaoDisplayStatus } from '../api';
import { MULTI_SELECT_SX } from './filterStyles';
import { toArray } from './filterUtils';

export const SAO_DISPLAY_STATUS_OPTIONS: { value: SaoDisplayStatus; label: string }[] = [
    { value: 'processing', label: 'Processing' },
    { value: 'major_issues', label: 'Major Issues' },
    { value: 'major_issues_reviewed', label: 'Major Issues (Reviewed)' },
    { value: 'minor_issues', label: 'Minor Issues' },
    { value: 'minor_issues_reviewed', label: 'Minor Issues (Reviewed)' },
    { value: 'likely_ok', label: 'Likely OK' },
    { value: 'failed', label: 'Error' },
    { value: 'not_started', label: 'Not Started' },
];

interface SaoDisplayStatusMultiSelectProps {
    /** Selected SAO display statuses */
    value: SaoDisplayStatus[] | undefined;
    /** Callback when selection changes - receives array of statuses or undefined */
    onChange: (statuses: SaoDisplayStatus[] | undefined) => void;
    /** Label for the select */
    label?: string;
    /** Component size */
    size?: 'small' | 'medium';
    /** Keep dropdown inside the DOM tree (required when used inside a modal with focus traps) */
    disablePortal?: boolean;
}

function SaoDisplayStatusMultiSelectComponent({
    value,
    onChange,
    label = 'S&O Check Status',
    size = 'small',
    disablePortal,
}: SaoDisplayStatusMultiSelectProps) {
    const selectedStatuses = useMemo(() => toArray(value), [value]);

    const handleChange = useCallback(
        (event: SelectChangeEvent<SaoDisplayStatus[]>) => {
            const newValue = event.target.value as SaoDisplayStatus[];
            onChange(newValue.length > 0 ? newValue : undefined);
        },
        [onChange],
    );

    const handleChipDelete = useCallback(
        (statusToRemove: SaoDisplayStatus) => (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            const newStatuses = selectedStatuses.filter(s => s !== statusToRemove);
            onChange(newStatuses.length > 0 ? newStatuses : undefined);
        },
        [selectedStatuses, onChange],
    );

    return (
        <FormControl size={size} sx={MULTI_SELECT_SX}>
            <InputLabel>{label}</InputLabel>
            <Select
                multiple
                value={selectedStatuses}
                label={label}
                onChange={handleChange}
                input={<OutlinedInput label={label} />}
                MenuProps={disablePortal ? { disablePortal: true } : undefined}
                renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(status => {
                            const opt = SAO_DISPLAY_STATUS_OPTIONS.find(o => o.value === status);
                            return (
                                <Chip
                                    key={status}
                                    label={opt?.label ?? status}
                                    size="small"
                                    onDelete={handleChipDelete(status)}
                                    onMouseDown={e => e.stopPropagation()}
                                />
                            );
                        })}
                    </Box>
                )}
            >
                {SAO_DISPLAY_STATUS_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export const SaoDisplayStatusMultiSelect = memo(SaoDisplayStatusMultiSelectComponent);

/** Get display label for an SAO display status */
export function getSaoDisplayStatusLabel(status: SaoDisplayStatus): string {
    return SAO_DISPLAY_STATUS_OPTIONS.find(o => o.value === status)?.label ?? status;
}
